import { useState } from 'react';
import clsx from 'clsx';
import { Trash } from 'ui/icons/icons/Actions/Trash';
import { UnstyledButton } from 'ui/components/ButtonV2/Button';
import { useTranslation } from '@whoop/i18n/lang/client';
import { updateLineItemQuantity } from '@/actions/cartActions';
import useLocale from '@/hooks/useLocale';
import { useCartStore } from '@/stores/StoreProvider';

interface IQuantitySelectorProps {
  quantity: number;
  min: number;
  max: number;
  cartId: string;
  itemId: string;
}
const QuantitySelector = ({
  quantity,
  min,
  max,
  cartId,
  itemId,
}: IQuantitySelectorProps) => {
  const { language } = useLocale();
  const { t } = useTranslation('cart');
  const { setCart } = useCartStore((state) => ({
    setCart: state.setCart,
  }));
  const [isLoading, setIsLoading] = useState(false);

  const handleUpdateQuantity = async (newQuantity: number) => {
    try {
      setIsLoading(true);
      const newCart = await updateLineItemQuantity({
        cartId,
        lineItemId: itemId,
        language,
        quantity: newQuantity,
      });
      setCart(newCart);
    } catch (error) {
      console.error('Error updating quantity:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={clsx(
        'flex max-w-24 items-center rounded border',
        isLoading && 'opacity-10',
      )}
    >
      {quantity === 1 ? (
        <UnstyledButton
          className='text-gray-b50 h-[30px] w-8 px-2 hover:bg-gray-50'
          onClick={() => handleUpdateQuantity(0)}
          title={t('removeItem')}
        >
          <Trash fill='black' height={16} width={20} />
        </UnstyledButton>
      ) : (
        <UnstyledButton
          className='w-8 px-2 py-1 text-gray-600 hover:bg-gray-100'
          disabled={quantity <= min}
          onClick={() => handleUpdateQuantity(quantity - 1)}
        >
          -
        </UnstyledButton>
      )}
      <span className='px-3 py-1 font-bold'>{quantity}</span>
      <UnstyledButton
        className='w-8 px-2 py-1 text-gray-600 hover:bg-gray-100'
        disabled={quantity >= max}
        onClick={() => handleUpdateQuantity(quantity + 1)}
      >
        +
      </UnstyledButton>
    </div>
  );
};

export default QuantitySelector;
